import { useEffect, useMemo, useState } from "react";
import { Building2, Check, ChevronsUpDown, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const PLAN_OPTIONS = [
  { id: "demo", label: "Demo" },
  { id: "basic", label: "Basic" },
  { id: "pro", label: "Pro" },
  { id: "premium", label: "Premium" },
];

/**
 * Super Admin–only dropdown that lets `hei@clientflow.no` pick any customer
 * business to manage. Selection is persisted in localStorage (handled by
 * AuthContext + api.js) and applied via the `X-Admin-Business-ID` header on
 * every backend call. Picking "Min konto (ClientFlow HQ)" returns the admin
 * to their own context.
 *
 * Renders nothing for any non-platform-admin user.
 */
export default function BusinessSwitcher({ className }) {
  const { isPlatformAdmin, adminBusinessId, setAdminBusinessId, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  // Local draft of the plan note for the currently-selected business. Kept
  // in component state so typing doesn't trigger a re-fetch on every keystroke;
  // we sync it back to the items list once the PUT succeeds.
  const [planNoteDraft, setPlanNoteDraft] = useState("");
  const [planNoteSaving, setPlanNoteSaving] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/businesses");
      setItems(Array.isArray(res.data?.items) ? res.data.items : []);
      setLoaded(true);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  // Lazy-load on first open so we don't waste a request for admins who never
  // touch the switcher. Re-fetch every time the popover opens so newly
  // onboarded businesses surface immediately.
  useEffect(() => {
    if (open) fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const homeBiz = useMemo(
    () => items.find((b) => b.is_admin) || null,
    [items]
  );
  const activeBiz = useMemo(() => {
    if (!adminBusinessId) return homeBiz;
    return items.find((b) => b.id === adminBusinessId) || null;
  }, [items, adminBusinessId, homeBiz]);

  const label = activeBiz?.name || (loaded ? "ClientFlow HQ" : (user?.name || "ClientFlow"));

  // Whichever business the admin is currently scoped to (own or impersonated).
  // Computed before any early-return so React's hook order stays consistent.
  const planTarget = !adminBusinessId
    ? null
    : items.find((b) => b.id === adminBusinessId) || null;
  const targetNote = planTarget?.plan_note || "";

  // Sync the textarea draft whenever the active business (or its server-side
  // note) changes. Compare-and-set so user typing isn't clobbered on every
  // re-render. Must come before the conditional return so hook order stays
  // stable for non-admin users (they bail at the next line).
  useEffect(() => {
    setPlanNoteDraft(targetNote);
  }, [planTarget?.id, targetNote]);

  if (!isPlatformAdmin) return null;

  const handleSelect = (bizId, isAdminBiz) => {
    setOpen(false);
    // Selecting the admin's own business clears the impersonation header.
    const next = isAdminBiz ? null : bizId;
    if (next === adminBusinessId) return; // no-op
    setAdminBusinessId(next);
    // Hard reload so every page/component re-fetches with the new scope.
    // Simpler & safer than chasing every cache + query down the tree.
    window.location.reload();
  };

  const changePlan = async (planId) => {
    if (!planTarget || planTarget.plan === planId) return;
    setPlanSaving(true);
    try {
      await api.put(`/admin/businesses/${planTarget.id}/plan`, { plan: planId });
      // Mirror the change locally so the badge updates without a refetch.
      setItems((prev) =>
        prev.map((b) => (b.id === planTarget.id ? { ...b, plan: planId } : b))
      );
      toast.success(`Plan endret til ${planId.charAt(0).toUpperCase() + planId.slice(1)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke endre plan");
    } finally {
      setPlanSaving(false);
    }
  };

  const savePlanNote = async () => {
    if (!planTarget) return;
    const trimmed = planNoteDraft.slice(0, 500);
    if (trimmed === (planTarget.plan_note || "")) return; // no-op
    setPlanNoteSaving(true);
    try {
      const res = await api.put(
        `/admin/businesses/${planTarget.id}/plan-note`,
        { plan_note: trimmed }
      );
      setItems((prev) =>
        prev.map((b) =>
          b.id === planTarget.id ? { ...b, plan_note: res.data?.plan_note ?? trimmed } : b
        )
      );
      toast.success("Pakkenotat lagret");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke lagre notat");
    } finally {
      setPlanNoteSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="admin-business-switcher-trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          className={cn(
            "group flex w-full items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-left text-xs font-medium transition-colors hover:bg-accent",
            className
          )}
        >
          <ShieldCheck className="w-4 h-4 text-primary shrink-0" strokeWidth={1.75} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Admin · administrer
            </div>
            <div className="truncate text-sm font-semibold text-foreground" data-testid="admin-business-switcher-active-name">
              {label}
            </div>
          </div>
          <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start" sideOffset={6}>
        <Command shouldFilter={true}>
          <CommandInput
            placeholder="Søk etter bedrift…"
            data-testid="admin-business-switcher-search"
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Laster bedrifter…
              </div>
            ) : (
              <>
                <CommandEmpty>Fant ingen bedrift</CommandEmpty>
                {homeBiz && (
                  <CommandGroup heading="ClientFlow">
                    <CommandItem
                      key={homeBiz.id}
                      value={`min-konto ${homeBiz.name}`}
                      onSelect={() => handleSelect(homeBiz.id, true)}
                      data-testid="admin-business-switcher-item-home"
                    >
                      <ShieldCheck className="w-4 h-4 mr-2 text-primary" />
                      <span className="flex-1 truncate">Min konto ({homeBiz.name})</span>
                      {!adminBusinessId && <Check className="w-4 h-4 text-primary" />}
                    </CommandItem>
                  </CommandGroup>
                )}
                <CommandGroup heading="Kunder">
                  {items
                    .filter((b) => !b.is_admin)
                    .map((b) => {
                      const selected = adminBusinessId === b.id;
                      return (
                        <CommandItem
                          key={b.id}
                          value={`${b.name} ${b.slug}`}
                          onSelect={() => handleSelect(b.id, false)}
                          data-testid={`admin-business-switcher-item-${b.slug}`}
                        >
                          <Building2 className="w-4 h-4 mr-2 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{b.name}</div>
                            <div className="truncate text-[10px] text-muted-foreground">
                              {b.slug}
                            </div>
                          </div>
                          <span
                            className="ml-2 px-1.5 py-0.5 rounded-full bg-muted text-[10px] uppercase tracking-wider text-muted-foreground"
                            data-testid={`admin-business-switcher-plan-${b.slug}`}
                          >
                            {b.plan}
                          </span>
                          {selected && <Check className="w-4 h-4 ml-1 text-primary" />}
                        </CommandItem>
                      );
                    })}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
        {planTarget ? (
          <div
            className="border-t border-border bg-muted/30 px-3 py-2.5"
            data-testid="admin-plan-footer"
          >
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                Endre plan
              </p>
              <p
                className="text-[11px] text-muted-foreground truncate ml-2 min-w-0"
                title={planTarget.name}
                data-testid="admin-plan-footer-business"
              >
                {planTarget.name}
              </p>
            </div>
            <div className="flex gap-1.5" role="radiogroup" aria-label="Velg plan">
              {PLAN_OPTIONS.map((p) => {
                const active = planTarget.plan === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={planSaving}
                    onClick={() => changePlan(p.id)}
                    className={cn(
                      "flex-1 rounded-md text-xs font-medium px-2 py-1.5 transition-colors border",
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground/80 border-border hover:bg-accent",
                      planSaving && "opacity-60 cursor-not-allowed"
                    )}
                    data-testid={`admin-plan-set-${p.id}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[10px] text-muted-foreground">
              Manuell pakkestyring (ingen fakturering).
            </p>
            <div className="mt-3 pt-2.5 border-t border-border/60">
              <label
                htmlFor="admin-plan-note-input"
                className="block text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1"
              >
                Internt pakkenotat
              </label>
              <textarea
                id="admin-plan-note-input"
                data-testid="admin-plan-note-input"
                value={planNoteDraft}
                onChange={(e) => setPlanNoteDraft(e.target.value.slice(0, 500))}
                placeholder="F.eks. Pilotkunde – Pro til Basic-pris i 3 mnd"
                rows={2}
                maxLength={500}
                className="w-full text-xs rounded-md border border-border bg-background px-2 py-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
              <div className="flex items-center justify-between mt-1.5">
                <span className="text-[10px] text-muted-foreground">
                  {planNoteDraft.length}/500 · kun for ClientFlow-admin
                </span>
                <button
                  type="button"
                  onClick={savePlanNote}
                  disabled={
                    planNoteSaving ||
                    planNoteDraft === (planTarget.plan_note || "")
                  }
                  data-testid="admin-plan-note-save"
                  className={cn(
                    "rounded-md text-xs font-medium px-2.5 py-1 transition-colors border",
                    "bg-primary text-primary-foreground border-primary hover:bg-primary/90",
                    "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary"
                  )}
                >
                  Lagre
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Helpful nudge for the case where the admin is on their own
          // ClientFlow HQ context and doesn't yet realise the plan controls
          // appear once a customer is selected.
          <div
            className="border-t border-border bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground text-center"
            data-testid="admin-plan-footer-hint"
          >
            Velg en kunde-bedrift for å endre plan og legge til pakkenotat.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
